import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { useAuth } from "../../../contexts/AuthContext";

const ClustersTab = ({
  clusters,
  projects,
  teams,
  teamMembers,
  onAddCluster,
  onUpdateCluster,
  onDeleteCluster,
}) => {
  const { user } = useAuth();
  const [isAddingCluster, setIsAddingCluster] = useState(false);
  const [editingClusterId, setEditingClusterId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    project_id: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [expandedClusters, setExpandedClusters] = useState(new Set());

  useEffect(() => {
    if (isAddingCluster && !editingClusterId) {
      const selectedProject = localStorage.getItem("selectedProject");
      const projectId =
        selectedProject && selectedProject !== "all"
          ? selectedProject
          : user?.project_id || null;
      setFormData({ name: "", project_id: projectId || "" });
    }
  }, [isAddingCluster, editingClusterId, user]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.name?.trim()) newErrors.name = "Cluster name is required";
    if (!formData?.project_id) {
      newErrors.project_id = "Please select a project from the header";
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (validateForm()) {
      try {
        setLoading(true);
        if (editingClusterId) {
          await onUpdateCluster(editingClusterId, formData);
          setEditingClusterId(null);
        } else {
          await onAddCluster(formData);
          setIsAddingCluster(false);
        }
        setFormData({ name: "", project_id: "" });
        setErrors({});
      } catch (err) {
        // Error handling is done in parent component
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDelete = async (clusterId) => {
    if (window.confirm("Are you sure you want to delete this cluster? This will also delete all associated teams and members.")) {
      try {
        setDeletingId(clusterId);
        await onDeleteCluster(clusterId);
      } catch (err) {
        // Error handling is done in parent component
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleEdit = (cluster) => {
    setFormData({
      name: cluster?.name,
      project_id: cluster?.project_id || "",
    });
    setEditingClusterId(cluster?.id);
    setIsAddingCluster(false);
  };

  const handleCancel = () => {
    setIsAddingCluster(false);
    setEditingClusterId(null);
    setFormData({ name: "", project_id: "" });
    setErrors({});
  };

  const getClusterStats = (clusterId) => {
    const clusterTeams = teams?.filter((t) => t.cluster_id === clusterId) || [];
    const clusterMembers = teamMembers?.filter((m) => m.cluster_id === clusterId) || [];
    return {
      teams: clusterTeams.length,
      members: clusterMembers.length,
    };
  };

  const toggleCluster = (clusterId) => {
    setExpandedClusters((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(clusterId)) {
        newSet.delete(clusterId);
      } else {
        newSet.add(clusterId);
      }
      return newSet;
    });
  };

  const getClusterMembers = (clusterId) => {
    return teamMembers?.filter((m) => m.cluster_id === clusterId) || [];
  };

  const getMemberTeam = (member) => {
    if (member?.team_id) {
      return teams?.find((t) => t.id === member.team_id);
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-foreground">Clusters</h3>
          <p className="text-sm text-muted-foreground">
            Manage installation clusters
          </p>
        </div>
        <Button
          variant="default"
          iconName="Plus"
          iconPosition="left"
          onClick={() => {
            setIsAddingCluster(true);
            setEditingClusterId(null);
            setFormData({ name: "", project_id: "" });
            setErrors({});
          }}
        >
          Add Cluster
        </Button>
      </div>

      {/* Add / Edit Modal */}
      {(isAddingCluster || editingClusterId) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="MapPin" size={20} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    {editingClusterId ? "Edit Cluster" : "Add Cluster"}
                  </h2>
                </div>
              </div>
              <button
                onClick={handleCancel}
                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"
                disabled={loading}
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {!formData?.project_id && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    Please select a project from the header dropdown first.
                  </p>
                </div>
              )}
              {formData?.project_id && (
                <div className="p-3 bg-primary/10 rounded-lg">
                  <p className="text-sm text-foreground">
                    <span className="font-medium">Project:</span>{" "}
                    {projects?.find((p) => p?.id === formData?.project_id)
                      ?.name || "N/A"}
                  </p>
                </div>
              )}

              <Input
                label="Cluster Name"
                type="text"
                placeholder="e.g., North Region Cluster"
                value={formData?.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e?.target?.value });
                  if (errors?.name) setErrors({ ...errors, name: "" });
                }}
                error={errors?.name}
                required
              />

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  loading={loading}
                  disabled={loading}
                >
                  {editingClusterId ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Clusters List */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {clusters?.length > 0 ? (
          <div className="divide-y divide-border">
            {clusters.map((cluster) => {
              const stats = getClusterStats(cluster.id);
              const isExpanded = expandedClusters.has(cluster.id);
              const clusterMembers = getClusterMembers(cluster.id);
              return (
                <div
                  key={cluster.id}
                  className="transition-colors"
                >
                  <div
                    className="p-4 hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => toggleCluster(cluster.id)}
                  >
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <Icon 
                            name={isExpanded ? "ChevronDown" : "ChevronRight"} 
                            size={16} 
                            className="text-muted-foreground flex-shrink-0" 
                          />
                          <Icon name="MapPin" size={20} className="text-primary flex-shrink-0" />
                          <h4 className="text-base font-semibold text-foreground truncate">
                            {cluster.name}
                          </h4>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 md:gap-4 text-sm text-muted-foreground ml-8">
                          <div className="flex items-center gap-2">
                            <Icon name="Folder" size={14} className="flex-shrink-0" />
                            <span className="truncate">
                              {projects?.find((p) => p?.id === cluster?.project_id)
                                ?.name || "N/A"}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Icon name="UserCog" size={14} className="flex-shrink-0" />
                            <span>{stats.teams} Teams</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Icon name="Users" size={14} className="flex-shrink-0" />
                            <span>{stats.members} Members</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Edit2"
                          onClick={() => handleEdit(cluster)}
                          disabled={deletingId === cluster.id}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Trash2"
                          onClick={() => handleDelete(cluster.id)}
                          loading={deletingId === cluster.id}
                          disabled={deletingId === cluster.id}
                          className="text-destructive hover:text-destructive"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Team Members List */}
                  {isExpanded && (
                    <div className="bg-muted/30 border-t border-border">
                      <div className="p-4">
                        <h5 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                          <Icon name="Users" size={16} className="text-primary" />
                          Team Members ({clusterMembers.length})
                        </h5>
                        {clusterMembers.length > 0 ? (
                          <div className="space-y-2">
                            {clusterMembers.map((member) => {
                              const team = getMemberTeam(member);
                              return (
                                <div
                                  key={member.id}
                                  className="p-3 bg-card border border-border rounded-lg hover:bg-muted/50 transition-colors"
                                >
                                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                        <Icon
                                          name="User"
                                          size={16}
                                          className="text-primary"
                                        />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                          <span className="text-sm font-medium text-foreground truncate">
                                            {member.name || "Unnamed User"}
                                          </span>
                                          <span className="px-2 py-0.5 rounded-lg bg-primary/10 text-primary text-xs font-medium whitespace-nowrap">
                                            {member.role === "super_admin" ||
                                            member.role === "project_admin"
                                              ? "Admin"
                                              : member.role === "cluster_manager"
                                              ? "Cluster Manager"
                                              : member.role?.charAt(0).toUpperCase() +
                                                  member.role?.slice(1) || "Staff"}
                                          </span>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs text-muted-foreground mt-1">
                                          <span className="truncate">{member.unique_id || "N/A"}</span>
                                          {team && (
                                            <span className="flex items-center gap-1 truncate">
                                              <Icon name="UserCog" size={12} className="flex-shrink-0" />
                                              {team.name}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="p-4 text-center text-sm text-muted-foreground">
                            <Icon
                              name="Users"
                              size={24}
                              className="mx-auto mb-2 text-muted-foreground"
                            />
                            No team members assigned to this cluster yet.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-12 text-center">
            <Icon
              name="MapPin"
              size={48}
              className="mx-auto mb-4 text-muted-foreground"
            />
            <p className="text-sm text-muted-foreground">
              No clusters added yet. Click "Add Cluster" to create one.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClustersTab;
